/* eslint-disable max-lines */
import {
  Component, ElementRef, Inject, ViewChild, ViewEncapsulation, Input, Output, 
  EventEmitter, ChangeDetectionStrategy, OnInit, OnDestroy
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router, Params } from '@angular/router';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Observable, Subscription, combineLatest, iif, of } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import isEmpty from 'lodash-es/isEmpty';

import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { HotelDetailFacade } from '@hotels/hotel-detail/hotel-detail.facade';
import { HotelDetailResponse, HotelAmenity } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import { MapMarkerInputs } from '@shared/map/constants/map.constants';
import { SearchType } from '@search-bar/entities/search-type';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { PAGE_SECTIONS } from '@hotels/hotel-detail/entities/page-sections';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { CLASS_TYPES } from '@ux/constants/travel-loader.constants';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { PackagesResultFacade } from '@packages/packages-result/store/packages-result.facade';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { getAmmenities } from '@app/mappers/ammenities.mapper';
import { isAnInternationalTripBySearch } from '@app/mappers/is-international-trip.mapper';
import { AvoService } from '@avo/services/avo.service';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { HotelGalleryMobileComponent } from '@ux/components/image-gallery/image-gallery-mobile/image-gallery-mobile.component';
import { BackgroundSurfaceType } from '@design-system/components/atoms/background-surface/entities/background-surface.types';

@Component({
  selector: 'app-hotel-detail-mobile',
  templateUrl: './hotel-detail-mobile.component.html',
  styleUrls: ['./hotel-detail-mobile.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HotelDetailMobileComponent implements OnInit, OnDestroy {

  @Input() showInHotelResult: boolean = false;
  @Input() hotelDetailParams: ParamMap;
  @Input() headerSticky: boolean = false;
  @Input() source: EVENT_SOURCE | string = EVENT_SOURCE.DETAIL;
  @Input() hasRappiCard: boolean;
  @Input() isPrime: boolean;
  @Input() isLoggedIn: boolean;
  @Input() totalNightsNumber: number;
  
  @Output() redirectToSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleAmenities: EventEmitter<void> = new EventEmitter<void>();
  @Output() goToGallery: EventEmitter<void> = new EventEmitter<void>();

  public showMap: boolean = false;
  public showPanel: boolean = false;
  public typeImageFlight: string = IMAGE_TYPE.HOTEL;
  public searchType: SearchType = SearchType.HOTELS_DETAIL;
  public loaderClassType: string = CLASS_TYPES.TOP;
  public showCalendar: boolean = false;
  public showHotelDetail: boolean = false;
  public showRoomsSwipe: boolean = false;
  public resetBoxTitlesPosition: number = 0;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  private calendarRoute: string = 'calendar';
  private currentSection: PAGE_SECTIONS;
  private readonly subscriptions = new Subscription();

  public readonly surface = BackgroundSurfaceType.Primary;

  @ViewChild('container') public container: ElementRef;
  @ViewChild('gallery') public gallery: ElementRef;
  @ViewChild('mainImage') public mainImage: ElementRef;
  @ViewChild('generalAnchor') generalAnchor: ElementRef;
  @ViewChild('aboutAnchor') aboutAnchor: ElementRef;
  @ViewChild('amenitiesAnchor') amenitiesAnchor: ElementRef;
  @ViewChild('locationAnchor') locationAnchor: ElementRef;
  @ViewChild('roomsAnchor') roomsAnchor: ElementRef;

  @ViewChild(HotelGalleryMobileComponent) imageGallery: HotelGalleryMobileComponent;

  constructor(
    private route: ActivatedRoute,
    private facade: HotelDetailFacade,
    private packagesResultFacade: PackagesResultFacade,
    private router: Router,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private translateService: TranslocoService,
    private domManipuleService: DomManipulateService,
    private triggerAmplitudeService: TriggerAmplitudeService,
    private avoService: AvoService,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
  }

  ngOnInit(): void {
    this.domManipuleService.resetScrollPosition();
    this.subscriptions.add(
      this.isByhours$
        .pipe(
          filter(isByhours => isByhours),
          tap(() => this.searchType = SearchType.BYHOURS_DETAIL)
        ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get params(): Params {
    return this.route.snapshot.params;
  }

  get isPackageDetail$(): Observable<boolean> {
    return this.baseSearchBarFacade.packageStep$.pipe(map(packageStep =>
      !!packageStep && packageStep === PackageStep.AIR_PACK_HOTEL_DETAIL));
  }

  get hotelAmmenities(): HotelAmenity[] {
    return getAmmenities() as HotelAmenity[];
  }

  get hotelDetail$(): Observable<HotelDetailResponse> | null {
    return this.facade.hotelDetail$.pipe(map(detail => detail.Info || detail.Result ? detail : null));
  }

  get isByhours$(): Observable<boolean> {
    return this.hotelDetail$.pipe(map( detail => detail?.Result?.MicroStay ));
  }

  get hourType$(): Observable<string[]> {
    return this.baseSearchBarFacade.hourType$;
  }

  get isLoadingHotelDetail$(): Observable<boolean> {
    return this.facade.isLoadingHotelDetail$;
  }

  get hotelMarkers$(): Observable<MapMarkerInputs[]> {
    return this.facade.hotelMarkers$;
  }

  get hotelDetailSearched$(): Observable<HotelSearched> {
    return this.isPackageDetail$.pipe(
      switchMap(isPackageDetail => iif(
        () => !isPackageDetail,
        this.facade.hotelDetailSearched$,
        this.packagesResultFacade.packageHotelDetailSearched$,
      )),
      map(hotelDetailSearched => hotelDetailSearched)
    );
  }

  get hasHotelDetailSearched$(): Observable<boolean> {
    return this.hotelDetailSearched$.pipe(
      map(hotelDetailSearched => !isEmpty(hotelDetailSearched))
    )
  }

  get isInternational$(): Observable<boolean> {
    return combineLatest([this.baseSearchBarFacade.searchData$, this.isPackageDetail$]).pipe(
      map(([searchData, isPackageDetail]) => {
        const searchBy = isPackageDetail ? SearchBy.PACKAGES : SearchBy.HOTELS;
        return !!searchData && isAnInternationalTripBySearch(searchBy, searchData);
      })
    );
  }
  
  get labelNumber$(): Observable<string> {
    return this.isByhours$.pipe(
      map((isByHours) => {
        let unitKey;
        if (isByHours) {
          unitKey = 'HOTELS_MODULE.RESULT_HOTELS.ROOMS_FROM';
        } else {
          unitKey = `HOTELS_MODULE.RESULT_HOTELS.${this.totalNightsNumber > 1 ? 'NIGHTS_FROM' : 'NIGHT_FROM'}`;
        }
        const translatedUnit = this.translateService.translate(unitKey);
        return isByHours ? translatedUnit : `${this.totalNightsNumber} ${translatedUnit}`;
      })
    );
  }
  
  public toggleMap(): void {
    this.showMap = !this.showMap;
    this.domManipuleService.setBodyScroll(!this.showMap);
    this.toggleAmenities.emit();
  }

  public toggleOpenGallery(): void {
    this.imageGallery?.toggleOpen();
  }

  public openGalleryFromDescription(): void {
    if (this.showInHotelResult) {
      this.goToGallery.emit();
    }
    this.imageGallery?.toggleOpen();
    this.resetBoxTitlesPosition++;
  }

  private disabledScoll(): void {
    this.domManipuleService.setBodyScroll(false);
  }

  private enabledScoll(): void {
    this.domManipuleService.setBodyScroll(true);
  }

  public toggleSearchPanel(): void {
    if (this.showPanel) this.enabledScoll();
    else this.disabledScoll();
    this.toggleSearch.emit();
    this.toggleAmenities.emit();
    this.showPanel = !this.showPanel;
    if (this.showPanel) {
      this.isPackageDetail$.pipe(
        take(1),
        switchMap((isPackageDetail: boolean) => iif(
          () => !!isPackageDetail,
          this.triggerAmplitudeService.trackAmplitudeEventProps$(
            AVO_EVENTS.VIEW_TRAVEL_SEARCH_SCREEN, 
            {
              SOURCE: this.showInHotelResult ? EVENT_SOURCE.DETAIL : this.source,
              TRAVEL_PRODUCT_TYPE: SearchBy.PACKAGES
            }
          ).pipe(
            tap((triggerView) => {
              this.AvoDispatch.viewTravelSearchScreen({
                ...this.avoService.avoBaseProperties(triggerView),
                source: this.avoService.getAvoSourceValueType(this.showInHotelResult ? EVENT_SOURCE.DETAIL : this.source),
                travelProductType: SearchBy.PACKAGES
              });
            })
          ),
          of(null)
        ))
      ).subscribe().unsubscribe();
    }
  }

  public isActiveSection(section: PAGE_SECTIONS): boolean {
    return this.currentSection === section;
  }

  public showMobileCalendar(type: CalendarField): void {
    this.showCalendar = true;
    this.showHotelDetail = false;
    const params = {
      activeType: type, searchType: this.searchType,
      departurePlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.DEPART',
      returnPlaceholder: 'SEARCH_MODULE.LOCATION_SEARCH.RETURN'
    };
    this.router.navigate(
      [{ outlets: { search: [this.calendarRoute] } }],
      {
        queryParams: {
          ...params,
          validateWithParams: true
        },
        state: params,
        relativeTo: this.route,
        skipLocationChange: true
      });
  }

  public redirectToSearchHotelDetail(): void {
    this.toggleSearchPanel();
    if (!this.showPanel) {
      this.redirectToSearch.emit();
    }
  }

  public showRooms(): void {
    if (this.headerSticky) {
      this.headerSticky = !this.headerSticky;
    }
    this.toggleSearch.emit();
    this.disabledScoll();
    this.showCalendar = false;
    this.showHotelDetail = true;
    this.showRoomsSwipe = true;
  }

  public toogleShowRooms(): void {
    this.showRoomsSwipe = !this.showRoomsSwipe;
  }

  public deactivateRooms(): void {
    this.toggleSearch.emit();
    this.enabledScoll();
  }
}
